import { ReservationState, TablePriority } from '@core/types'
import { find, get, orderBy, sortBy } from 'es-toolkit/compat'

/**
 * Sort categories by sortOrder
 * @param categories Categories to sort
 * @returns A list of sorted categories
 */
export function sortCategories<T extends { sortOrder: number }>(categories: T[]): T[] {
	return orderBy(categories, (category) => category.sortOrder, ['asc'])
}

/**
 * Sort menu items by sortOrder within a category
 * @param items Items to sort
 * @param categoryId Id of the category whose items will be sorted
 * @returns A list of sorted items
 */
export function sortMenuItemsInCategory<
	T extends { categories: K[] },
	K extends { categoryId: string; sortOrder: number },
>(items: T[], categoryId: string): T[] {
	return sortBy(items, (item) => get(find(item.categories, { categoryId }), 'sortOrder', 0))
}

/**
 * Sort menu items by sort order of given categories and sort order of items within categories
 * @param items Items to sort
 * @param categories List of categories to sort items within
 * @returns A list of sorted items
 */
export function sortMenuItemsByCategories<
	T extends { categories: { categoryId: string; sortOrder: number }[] },
	K extends { id: string; sortOrder: number },
>(items: T[], categories: K[]): T[] {
	return sortBy(
		items,
		(item) =>
			item.categories
				.map((category) => {
					const categoryIndex = categories.findIndex((cat) => cat.id === category.categoryId)
					return `${categoryIndex}-${category.sortOrder}`
				})
				.join('-'), // Multi-criteria sorting key
	)
}

/**
 * Sort tables by their starting positions on room plan grid
 * @param tables List of tables to sort
 * @returns List of sorted tables
 */
export function sortTablesByRoomPlan<T extends { rowStart: number; columnStart: number; width: number }>(
	tables: T[],
): T[] {
	return sortBy(tables, [
		(table) => table.rowStart, // Primary sort by rowStart
		(table) => table.columnStart + table.width, // Secondary sort by the end position
	])
}

/**
 * Sort tables by their capacity
 * @param tables List of tables to sort
 * @returns List of sorted tables
 */
export function sortTablesByCapacityAsc<T extends { capacity: number }>(tables: T[]): T[] {
	return sortBy(tables, 'capacity')
}

/**
 * Sort tables by priority from high to low
 * @param tables List of tables to sort
 * @returns List of sorted tables
 */
export function sortTablesByPriority<T extends { priority: keyof typeof TablePriority }>(tables: T[]): T[] {
	const priorityMap = {
		[TablePriority.high]: 1,
		[TablePriority.normal]: 2,
		[TablePriority.low]: 3,
	}

	return sortBy(tables, (table) => priorityMap[table.priority])
}

/**
 * Sort reservations by their state with following order:
 * Unconfirmed, Approved, Seated, Done, Rejected
 * @param reservations Array of reservations to sort
 * @returns Array of sorted reservations
 */
export function sortReservationsByState<T extends { state: keyof typeof ReservationState }>(reservations: T[]): T[] {
	const stateMap = {
		[ReservationState.unconfirmed]: 1,
		[ReservationState.approved]: 2,
		[ReservationState.seated]: 3,
		[ReservationState.done]: 4,
		[ReservationState.rejected]: 5,
	}

	return sortBy(reservations, (reservation) => stateMap[reservation.state])
}

/**
 * Sort table combinations by their capacity
 * @param tables List of table combinations to sort
 * @returns List of sorted table combinations
 */
export function sortTableCombinationsByCapacityAsc<T extends { capacity: number }>(tables: T[]): T[] {
	return sortBy(tables, 'capacity')
}
